// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";
import { LocationPicker } from "./naver/LocationPicker";

// 신설 시 지도 초기 중심 — 특정 권역이 없으므로 전국이 보이는 개관 뷰에서
// 시작해 운영자가 중심점을 클릭해 좁혀가게 한다.
const REGION_PICK_CENTER = { lat: 36.5, lng: 127.8 };

// form(center_latitude/longitude) ↔ LocationPicker(Coord) 어댑터.
const coordOf = (f) =>
  f.center_latitude != null && f.center_longitude != null
    ? { lat: f.center_latitude, lng: f.center_longitude } : null;

// Shared "create new region" modal used by the platform dashboard and the
// region-management screen. Both screens render this with the same UX:
//   - auto-numbered code (readonly), 5 editable fields
//   - inline error handling (validation / auth)
//   - onCreated callback so the parent can refresh its list
//
// EDIT-mode is intentionally NOT here — region-management has its own
// edit form that exposes policy fields (grade, pilot) plus a readonly
// stats panel. Adding edit here would couple two different surfaces.

const EMPTY = {
  code: "", name: "", area_description: "",
  operation_status: "operating", manager_name: "", manager_user_id: "",
  center_latitude: null, center_longitude: null,
};

export function RegionCreateModal({ onClose, onCreated }) {
  useEscapeKey(onClose);
  const [form, setForm] = React.useState(EMPTY);
  const [codeLoading, setCodeLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [adminUsers, setAdminUsers] = React.useState([]);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  // 지도 클릭/드래그로 중심 좌표를 직접 지정.
  const setCoord = (c) => setForm(f => ({ ...f, center_latitude: c.lat, center_longitude: c.lng }));

  React.useEffect(() => {
    fetch("/api/v1/regions/next_code", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (j?.code) setForm(f => f.code ? f : { ...f, code: j.code });
      })
      .catch(() => { /* fallback: user retries */ })
      .finally(() => setCodeLoading(false));

    // 미배정 관리자만 후보로 — 한 명이 둘 이상의 권역을 담당하지 못하게 한다.
    fetch("/api/v1/admins?role=region_admin&available_region_manager=1", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setAdminUsers(Array.isArray(list) ? list : []))
      .catch(() => setAdminUsers([]));
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    if (!form.code.trim()) return setErr("권역 코드를 불러오지 못했습니다. 다시 시도해주세요.");
    if (!form.name.trim()) return setErr("권역 이름을 입력해주세요.");
    setSubmitting(true);
    try {
      const r = await fetch("/api/v1/regions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ region: form }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 401)      setErr("로그인이 필요합니다.");
        else if (r.status === 403) setErr(j.message || "시스템 관리자 권한이 필요합니다.");
        else if (j.details?.[0]?.includes?.("Code has already")) setErr("이미 사용 중인 권역 코드입니다.");
        else                       setErr(apiErrorMessage(j, r.status));
        return;
      }
      onCreated?.(j);
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card" style={{width:560, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">새 권역 추가</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>시스템 관리자 전용 · 운영 통계는 자동 집계됩니다</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

        <div style={{padding:22, display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
          <div>
            <div className="label">권역 코드 * <span className="muted" style={{fontWeight:400, fontSize:10.5, marginLeft:4}}>자동 채번 · 수정 불가</span></div>
            <input className="input mono" value={form.code}
              readOnly aria-readonly="true" tabIndex={-1}
              title="권역 코드는 자동 채번되며 수정할 수 없습니다."
              placeholder={codeLoading ? "불러오는 중…" : ""}
              style={{background:'var(--bg-sub)', color:'var(--ink-3)', cursor:'not-allowed'}}/>
          </div>
          <div>
            <div className="label">권역 이름 *</div>
            <input className="input" value={form.name} onChange={e=>set('name')(e.target.value)} placeholder="예: 강원춘천권역" required/>
          </div>
          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">담당 행정구역</div>
            <input className="input" value={form.area_description} onChange={e=>set('area_description')(e.target.value)} placeholder="예: 강원 춘천시·홍천군"/>
          </div>
          <div style={{gridColumn:'1 / span 2'}}>
            <div className="label">권역 중심 좌표 <span className="muted" style={{fontWeight:400, fontSize:10.5, marginLeft:4}}>지도를 클릭해 중심점을 지정</span></div>
            <LocationPicker
              value={coordOf(form)}
              onChange={setCoord}
              center={REGION_PICK_CENTER}
              zoom={7}
              height={200}/>
            {coordOf(form)
              ? <div style={{marginTop:6, fontSize:11.5, color:'var(--green-600, #15803d)'}}>
                  ✓ 중심 좌표 지정됨 <b className="mono">{form.center_latitude.toFixed(5)}, {form.center_longitude.toFixed(5)}</b>
                </div>
              : <div className="muted" style={{marginTop:6, fontSize:11}}>지정하지 않으면 소속 기관·매니저·어르신 좌표 평균으로 자동 유추됩니다.</div>}
          </div>
          <div>
            <div className="label">운영 상태</div>
            <select className="select" value={form.operation_status} onChange={e=>set('operation_status')(e.target.value)}>
              <option value="operating">운영 중</option>
              <option value="pilot">시범 운영</option>
            </select>
          </div>
          <div>
            <div className="label">권역 관리자 계정</div>
            <select className="select" value={form.manager_user_id || ""}
              onChange={e => {
                const id = e.target.value;
                const u = adminUsers.find(a => String(a.id) === id);
                setForm(f => ({ ...f, manager_user_id: id ? Number(id) : "", manager_name: u ? u.name : f.manager_name }));
              }}>
              <option value="">— 미지정 (직접 입력) —</option>
              {adminUsers.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </div>
          <div>
            <div className="label">관리자 이름 (직접 입력)</div>
            <input className="input" value={form.manager_name}
              onChange={e=>set('manager_name')(e.target.value)}
              placeholder={form.manager_user_id ? "선택한 계정 이름을 자동 사용" : "예: 김도현"}
              disabled={!!form.manager_user_id}/>
          </div>
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button type="submit" className="btn btn-primary btn-md" disabled={submitting}>
            {submitting ? '추가 중…' : '권역 추가'}
          </button>
        </div>
      </form>
    </div>
  );
}
